import { Button, Col, Row, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import DocumentTable from "../components/generic_components/documentTable_components/DocumentTable";
import { DocumentStruct } from "../common/Interfaces";
import CheckBoxList from "../components/generic_components/CheckBoxList";
import { useState, useEffect } from "react";
import { getExcludedSerialNumbers, postExcludedSerialNumbers, deleteExcludedSerialNumbers } from "../api/simDocuments/ExcludedSerialNumberService";
import { useSerialNumbers } from "../api/sim/ModelService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleDown, faCircleUp } from "@fortawesome/free-solid-svg-icons";
import ErrorMessage from "../components/generic_components/ErrorMessage";
import SuccessPopUp from "../components/generic_components/SuccessPopUp";

export default function ExcludeSerialNumber() {
  const location = useLocation();
  const navigate = useNavigate();
  const excludeSerialNumberState = location.state?.excludeSerialNumberState;
  const selectedDocument: DocumentStruct = excludeSerialNumberState.document;
  const [unselectedSerialNumbers, setUnselectedSerialNumbers] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingExcludedSerialNumbers, setIsFetchingExcludedSerialNumbers] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);


  //keeps the serial numbers selected in the left checkbox list (linked serial numbers)
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState<string[]>([]);
  // keeps the serial numbers that will be posted as excluded when hitting save (right checkbox list)
  const [unlinkedSerialNumbers, setUnlinkedSerialNumbers] = useState<string[]>([]);
  // keeps the serial numbers that are currently excluded for this document (fetched from backend)
  const [excludedSerialNumbers, setExcludedSerialNumbers] = useState<string[]>([]);
  // keeps the serial numbers currently linked to this document
  const [linkedSerialNumbers, setLinkedSerialNumbers] = useState<string[]>([]);

  const { serialNumbers: fetchedSerialNumbers, loading: isFetchingSerialNumbers } = useSerialNumbers({
    modelCodes: (selectedDocument.models?.map(model => model.code) || []).join(","),
    productCompanyCode: selectedDocument.productCompany || ""
  });

  useEffect(() => {
    const fetchExcluded = async () => {
      setIsFetchingExcludedSerialNumbers(true);
      try {
        const fetchedExcludedSerialNumbers = await getExcludedSerialNumbers(selectedDocument.documentReference);

        setExcludedSerialNumbers(fetchedExcludedSerialNumbers || []);
        setUnlinkedSerialNumbers(fetchedExcludedSerialNumbers || []);
        setErrorMessages([]);
      } catch (e) {
        console.error(e);
        setErrorMessages(prev => [...prev, "Failed to fetch excluded serial numbers."]);
        setExcludedSerialNumbers([]);
      } finally {
        setIsFetchingExcludedSerialNumbers(false);
      }
    };
    fetchExcluded();
  }, [selectedDocument.documentReference]);

  useEffect(() => {
    setLinkedSerialNumbers(fetchedSerialNumbers.filter(sn => !excludedSerialNumbers.includes(sn)));
  }, [fetchedSerialNumbers, excludedSerialNumbers]);

  // Global loading state - true when fetching OR saving
  const loading = isFetchingSerialNumbers || isSaving || isFetchingExcludedSerialNumbers;

  const modifyLinkedItems = () => {
    let selectedItems = selectedSerialNumbers;
    setUnlinkedSerialNumbers(unlinkedSerialNumbers.concat(selectedItems));
    setLinkedSerialNumbers(linkedSerialNumbers.filter(item => !selectedItems.includes(item)));

    setSelectedSerialNumbers([]);
    setUnselectedSerialNumbers([]);
  }

  const modifyUnlinkedItems = () => {
    let unselectedItems = unselectedSerialNumbers;
    setLinkedSerialNumbers(unselectedSerialNumbers.concat(linkedSerialNumbers));
    setUnlinkedSerialNumbers(unlinkedSerialNumbers.filter(item => !unselectedItems.includes(item)));


    setUnselectedSerialNumbers([]);
    setSelectedSerialNumbers([]);
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const diffSerialNumbers = excludedSerialNumbers.filter(sn => !unlinkedSerialNumbers.includes(sn));
      // delete all excluded serial numbers that are not in the unlinkedSerialNumbers (i.e. the ones that were linked again)
      await deleteExcludedSerialNumbers(selectedDocument.documentReference, diffSerialNumbers);

      if(unlinkedSerialNumbers.length <= 0) {
        setIsSaving(false);
        return;
      }

      await postExcludedSerialNumbers({
        documentReference: selectedDocument.documentReference,
        serialNumbers: unlinkedSerialNumbers
      });

      setErrorMessages([]);
      setSaveSuccess(true);
    } catch (e) {
      console.error(e);
      setErrorMessages(prev => [...prev, "Failed to save excluded serial numbers."]);
    } finally {
      setIsSaving(false);
    }
  };
 

  return (
    <>
      <Row>
        <Col xs={12}>
          <h5>Document Information</h5>
        </Col>
      </Row>
      <Row className="padding-top">
        <Col xs={12}>
        <DocumentTable
          exclude={true}
          noAction={true}
          documentResults={[selectedDocument]}
        />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <h5 className="padding-top">Serial Number Linking</h5>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {errorMessages.length > 0 && <ErrorMessage errors={errorMessages} />}
          {saveSuccess && <SuccessPopUp showPopup={true} message="Successfully saved the serial number linking"/>}
        </Col>
      </Row>
      <Row className="padding-top justify-content-center align-items-sm-center">
        <Col xs={12} sm={5}>
          <Row>
            <Col style={{ fontWeight: "bold" }}>
              Link used in following serial numbers
            </Col>
          </Row>
          <CheckBoxList
            threeCharSearch={false}
            loading={isFetchingSerialNumbers}
            options={linkedSerialNumbers.map((serialNumber) => ({
              key: serialNumber,
              label: serialNumber,
            }))}
            selected={selectedSerialNumbers.map(key => ({ key, label: key }))}
            onChange={(selectedItems) => {
              setSelectedSerialNumbers(
                selectedItems?.map((item) => item.key) || []
              );
            }}
            selectAll={false}
          />
        </Col>
        <Col xs={12} sm={2} className="p-2 d-flex flex-sm-column justify-content-center align-items-center justify-content-sm-start">
            <Button className="btn btn-light rotate-sm-90" onClick={() => modifyLinkedItems()}>
              <span  style={{ fontSize: "11pt" }}>
                <FontAwesomeIcon icon={faCircleUp} />
              </span>
            </Button>
            <Button className="btn btn-light ms-2 ms-sm-0 mt-sm-2 rotate-sm-90" onClick={() => modifyUnlinkedItems()}>
              <span  style={{ fontSize: "11pt" }}>
                <FontAwesomeIcon icon={faCircleDown} />
              </span>
            </Button>
        </Col>
        <Col xs={12} sm={5}>
          <Row>
            <Col style={{ fontWeight: "bold" }}>
              Not linked serial numbers
            </Col>
          </Row>
          <CheckBoxList
            threeCharSearch={false}
            loading={isFetchingExcludedSerialNumbers}
            options={unlinkedSerialNumbers.map((serialNumber) => ({
              key: serialNumber,
              label: serialNumber,
            }))}
            selected={unselectedSerialNumbers.map(key => ({ key, label: key }))}
            onChange={(selectedItems) => {
              setUnselectedSerialNumbers(
                selectedItems?.map((item) => item.key) || []
              );
            }}
            selectAll={false}
          />
        </Col>
      </Row>
      <Row className="padding-top pb-2">
        <Col className="d-flex justify-content-end" xs={12}>
          <Button className="btn btn-secondary me-2" onClick={() => navigate(-1)}>Cancel</Button>
          <Button className="btn btn-primary" onClick={handleSave}>Save</Button>
          {isSaving && <Spinner animation="border" size="sm" className="ms-2" />}
        </Col>
      </Row>
    </>
  );
}
